/**
 * The alert severity vocabulary, and how it reaches a pill.
 *
 * There were two vocabularies and they did not match. The database and the helmet translator emit
 * `critical | high | medium | low | info` (`libs/mongodb/src/schemas/robots/Alert.schema.ts`), while
 * the console knew only `info | warning | critical` — and its pill mapper fell through to `info` for
 * anything it did not recognise.
 *
 * The consequences were both silent and serious. Every `high` and `medium` alarm — helmet removed,
 * left the work area, abnormal body temperature, altitude — was painted as INFORMATIONAL. And
 * because `AlertInbox` filtered on that same narrow union, a dashboard element configured with a
 * severity filter dropped every `high` alarm on the floor without saying so. `warning`, meanwhile,
 * is a value no backend has ever written, so the filter's middle option matched nothing at all.
 *
 * One vocabulary now, the backend's, because it is the one with writers.
 */

import type { StatusPillStatus } from '../../molecules/StatusPill/StatusPill'

/** Ordered most severe first — the order the inbox sorts by. */
export const ALERT_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const

export type AlertSeverity = (typeof ALERT_SEVERITIES)[number]

const KNOWN = new Set<string>(ALERT_SEVERITIES)

/**
 * Coerce whatever arrived into a severity we can rank and colour.
 *
 * `warning` is accepted and read as `high` purely because the console's own older union offered it,
 * so a saved dashboard element may still carry it in a `severityFilter`. It maps to `high` rather
 * than being dropped, so an existing filter keeps selecting the alarms the operator meant.
 *
 * Anything genuinely unrecognised becomes `info` — visible and rankable, never hidden. An alarm we
 * cannot classify is still an alarm.
 */
export function normalizeSeverity(severity: string | null | undefined): AlertSeverity {
  if (severity && KNOWN.has(severity)) return severity as AlertSeverity
  if (severity === 'warning') return 'high'

  return 'info'
}

/**
 * Severity → pill colour.
 *
 * `StatusPill` already speaks this exact vocabulary, so this is deliberately a pass-through after
 * normalisation rather than a second colour scheme to keep in step.
 */
export function severityToPill(severity: string | null | undefined): StatusPillStatus {
  return normalizeSeverity(severity)
}

/** Higher = more urgent. For sorting; not for display. */
export function severityRank(severity: string | null | undefined): number {
  return ALERT_SEVERITIES.length - ALERT_SEVERITIES.indexOf(normalizeSeverity(severity))
}
